import React, {
  useState,
  ReactElement,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import Head from 'next/head';
import { Tab } from '@headlessui/react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import cx from 'classnames';

import { IconButton } from 'components';
import { getLayout } from 'components/layouts/Header';
import Modal from 'components/Modal';
import {
  NoDiscussions,
  SocietyDiscussionCard,
  SocietyDiscussions,
} from 'components/candidate/discussions';
import { Badge } from 'components/ComponentV2';
import { useResponsive } from 'hooks/useResponsive';
import { withAuthCandidate } from 'lib/hocs/withAuthCandidate';
import {
  CandidateMessageProvider,
  useCandidateMessage,
} from 'lib/providers/CandidateMessageProvider';
import { RoomAttributes } from 'lib/models/candidate-messages';
import { TBasicStudentSocietyWithPostsStat } from 'lib/models/discussion';
import { DiscussionsProvider } from 'lib/providers/DiscussionsProvider';
import { DiscussionProvider } from 'lib/providers/DiscussionProvider';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import { ChatRoomCard } from './ChatRoomCard/ChatRoomCard.component';
import { ChatWindow } from './ChatWindow/ChatWindow.component';
import { MessagesEmptyState } from './MessagesEmptyState/MessagesEmptyState.component';
import { ParticipantsListModal } from './ParticipantsListModal/ParticipantsListModal.component';

import styles from './Messages.module.scss';

const TabGroupContent = ({
  unreadMessageCount,
}: {
  unreadMessageCount: number | undefined;
}) => {
  const [t] = useTranslation('messages');
  const router = useRouter();
  const screens = useResponsive();
  const { tab } = router.query;
  const [selectedTabIndex, setSelectedTabIndex] = useState(
    tab === 'discussions' ? 1 : 0
  );

  useEffect(() => {
    router.replace({
      query: {
        ...router.query,
        tab: selectedTabIndex === 0 ? 'messages' : 'discussions',
      },
    });
  }, [selectedTabIndex]);

  const {
    rooms,
    isRoomsFetching,
    activeRoom,
    showParticipantsModal,
    interactedSocieties,
    totalUnreadPostCount,
    selectedInteractedSociety,
    onRoomSelect,
    onClearActiveRoom,
    toggleParticipantsModal,
    onSelectInteractedSociety,
    onClearSelectedInteractedSociety,
  } = useCandidateMessage();

  const onTabChange = useCallback((index: number) => {
    setSelectedTabIndex(index);
  }, []);

  const hasNoRooms =
    selectedTabIndex === 0 && rooms.length === 0 && !isRoomsFetching;
  const hasNoDiscussions =
    selectedTabIndex === 1 && interactedSocieties?.length === 0;

  const chatWindowContent = useMemo(() => {
    return (
      <ChatWindow
        className={cx(styles['messages-chat-window'], {
          [styles['empty-chat']]: hasNoRooms,
        })}
        activeRoom={activeRoom}
        openParticipantsModal={toggleParticipantsModal}
      />
    );
  }, [activeRoom, hasNoRooms, toggleParticipantsModal]);

  return (
    <div className={styles.content}>
      <Tab.Group
        as="div"
        className={styles['tab-group']}
        selectedIndex={selectedTabIndex}
        onChange={onTabChange}
      >
        <div
          className={cx(styles['tab-list-container'], {
            [styles['full-width']]: hasNoRooms || hasNoDiscussions,
          })}
        >
          <IconButton
            iconName="icon_chevron-left"
            size="auto"
            className={styles['back-button']}
            onClick={router.back}
          />
          <Tab.List
            className={cx(styles['tab-list'], {
              [styles['empty-list']]: hasNoRooms || hasNoDiscussions,
            })}
          >
            <Tab className={styles.tab}>
              {t('tabs.0')}
              {unreadMessageCount ? (
                <Badge className={styles['unread-badge']}>
                  {unreadMessageCount}
                </Badge>
              ) : null}
            </Tab>
            <Tab className={styles.tab}>
              {t('tabs.1')}
              {totalUnreadPostCount > 0 && (
                <Badge className={styles['unread-badge']}>
                  {totalUnreadPostCount}
                </Badge>
              )}
            </Tab>
          </Tab.List>
        </div>
        <Tab.Panels className={styles['tab-panels']}>
          <Tab.Panel as="div" className={styles['tab-panel']}>
            {rooms.length > 0 ? (
              <div className={styles.cards}>
                {rooms.map((room: RoomAttributes) => (
                  <ChatRoomCard
                    key={room.id}
                    isActive={!!activeRoom && room.id === activeRoom.id}
                    room={room}
                    onSelect={onRoomSelect}
                  />
                ))}
              </div>
            ) : null}
            {hasNoRooms ? (
              <MessagesEmptyState />
            ) : screens.sm ? (
              <Modal
                isOpen={!!activeRoom}
                onRequestClose={onClearActiveRoom}
                isFullScreen
              >
                <IconButton
                  iconName="icon_chevron-left"
                  size="auto"
                  onClick={onClearActiveRoom}
                  className={styles['window-back-button']}
                />
                {chatWindowContent}
              </Modal>
            ) : (
              chatWindowContent
            )}
            <ParticipantsListModal
              open={showParticipantsModal && !!activeRoom}
              activeRoom={activeRoom}
              onClose={toggleParticipantsModal}
            />
          </Tab.Panel>
          <Tab.Panel className={styles['tab-panel']}>
            {interactedSocieties && interactedSocieties.length > 0 ? (
              <div className={styles.cards}>
                {interactedSocieties.map(
                  (society: TBasicStudentSocietyWithPostsStat) => (
                    <SocietyDiscussionCard
                      key={society.id}
                      className={styles.card}
                      society={society}
                      onSelect={onSelectInteractedSociety}
                      isSelected={selectedInteractedSociety?.id === society.id}
                    />
                  )
                )}
              </div>
            ) : null}
            {hasNoDiscussions ? (
              <NoDiscussions />
            ) : selectedInteractedSociety ? (
              <DiscussionsProvider
                studentSociety={selectedInteractedSociety}
                key={selectedInteractedSociety.id}
                isCandidatePage={true}
                skipAttachments={true}
                isSocietyMember={true}
              >
                <DiscussionProvider>
                  {screens.sm ? (
                    <Modal
                      isOpen={!!selectedInteractedSociety}
                      onRequestClose={onClearSelectedInteractedSociety}
                      isFullScreen
                    >
                      <IconButton
                        iconName="icon_chevron-left"
                        size="auto"
                        onClick={onClearSelectedInteractedSociety}
                        className={styles['window-back-button']}
                      />
                      <SocietyDiscussions />
                    </Modal>
                  ) : (
                    <SocietyDiscussions />
                  )}
                </DiscussionProvider>
              </DiscussionsProvider>
            ) : null}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

const MessagesPage = (): ReactElement => {
  const { candidateDashboard, totalUnreadPostCount } = useCandidateDashboard();
  const { unread_messages_count } = candidateDashboard || {};
  const totalUnreadCount =
    totalUnreadPostCount + (unread_messages_count ? unread_messages_count : 0);

  return (
    <>
      <Head>
        <title>
          {totalUnreadCount > 0
            ? `(${totalUnreadCount}) Messages | Huzzle`
            : `Messages | Huzzle`}
        </title>
        <meta name="description" content="Huzzle" />
      </Head>
      <CandidateMessageProvider>
        <section className={styles.container}>
          <TabGroupContent unreadMessageCount={unread_messages_count} />
        </section>
      </CandidateMessageProvider>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'candidate-onboarding',
        'messages',
        'discussions',
      ])),
    },
  };
}

const MessagesWrapper = withAuthCandidate(MessagesPage);

// @ts-ignore
MessagesWrapper.getLayout = getLayout;

export default MessagesWrapper;
